import { useState } from 'react';
import {
  CloseOutlined as CloseOutlinedIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  NotesOutlined as NotesOutlinedIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
} from '@mui/icons-material';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Paper,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';

import {
  useGetClinicDocumentSectionContentQuery,
  useGetClinicDocumentSectionQuery,
} from '../store';
import type { SectionSummaryResponse } from '../types';

import { DocumentMarkdownRenderer } from './DocumentMarkdownRenderer';

interface ClinicDocumentSectionTreeItemProps {
  section: SectionSummaryResponse;
  depth?: number;
}

export function ClinicDocumentSectionTreeItem({
  section,
  depth = 0,
}: ClinicDocumentSectionTreeItemProps) {
  const [childrenOpen, setChildrenOpen] = useState(false);
  const [contentOpen, setContentOpen] = useState(false);

  const hasChildren = section.has_children ?? false;

  const {
    data: detailData,
    isFetching: isFetchingChildren,
    error: childrenError,
  } = useGetClinicDocumentSectionQuery(
    { sectionId: section.clinic_document_section_id },
    {
      skip: !childrenOpen || !hasChildren,
    },
  );

  const {
    data: contentData,
    isFetching: isFetchingContent,
    error: contentError,
  } = useGetClinicDocumentSectionContentQuery(
    { sectionId: section.clinic_document_section_id },
    {
      skip: !contentOpen,
    },
  );

  const children = detailData?.children ?? [];

  return (
    <Box sx={{ pl: depth === 0 ? 0 : 2, mt: depth === 0 ? 0 : 1.5 }}>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 2,
          borderColor: depth === 0 ? 'divider' : 'grey.200',
          bgcolor: depth === 0 ? 'background.paper' : 'grey.50',
        }}
      >
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {section.title}
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                <Chip size="small" variant="outlined" label={section.node_type} />
                {hasChildren && (
                  <Chip size="small" color="primary" variant="outlined" label="Has children" />
                )}
              </Stack>
            </Box>

            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<VisibilityOutlinedIcon />}
                onClick={() => setContentOpen(true)}
              >
                View content
              </Button>
              {hasChildren && (
                <Button
                  size="small"
                  variant={childrenOpen ? 'contained' : 'outlined'}
                  startIcon={childrenOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={() => setChildrenOpen(current => !current)}
                >
                  {childrenOpen ? 'Hide children' : 'Browse children'}
                </Button>
              )}
            </Stack>
          </Stack>

          {section.summary && (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              {section.summary}
            </Typography>
          )}

          <Dialog
            fullScreen
            open={contentOpen}
            onClose={() => setContentOpen(false)}
          >
            <AppBar sx={{ position: 'relative' }} color="default" elevation={1}>
              <Toolbar>
                <IconButton
                  edge="start"
                  onClick={() => setContentOpen(false)}
                  aria-label="Close content viewer"
                >
                  <CloseOutlinedIcon />
                </IconButton>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 1, flex: 1 }}>
                  <NotesOutlinedIcon fontSize="small" color="action" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {contentData?.title ?? section.title}
                  </Typography>
                </Stack>
              </Toolbar>
            </AppBar>
            <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 2, md: 4 }, maxWidth: 900, mx: 'auto', width: '100%' }}>
              {isFetchingContent ? (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 4 }}>
                  <CircularProgress size={22} />
                  <Typography variant="body2" color="text.secondary">
                    Loading section content...
                  </Typography>
                </Stack>
              ) : contentError ? (
                <Alert severity="error">Failed to load section content.</Alert>
              ) : (
                <DocumentMarkdownRenderer
                  markdown={contentData?.content_md ?? ''}
                />
              )}
            </Box>
          </Dialog>

          {childrenOpen && hasChildren && (
            <Box sx={{ borderLeft: theme => `2px solid ${theme.palette.grey[200]}`, pl: 1.5 }}>
              {isFetchingChildren ? (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Loading child sections...
                  </Typography>
                </Stack>
              ) : childrenError ? (
                <Alert severity="error">Failed to load child sections.</Alert>
              ) : children.length ? (
                children.map(child => (
                  <ClinicDocumentSectionTreeItem
                    key={child.clinic_document_section_id}
                    section={child}
                    depth={depth + 1}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  No child sections were returned for this node.
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
